import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import DescriptionList from '@anwb/poncho/components/description-list';
import Typography from '@anwb/poncho/components/typography';
import { leaseCarDataNameSelector, leaseCarDataSpecsSelector } from 'store/selectors';

import { trackEvent } from '../../../../../utils/trackEvent';
import { getCarContractABTest } from '../../../utils/getCarContractABTest';

import { ModalOrBottomSheet } from './ModalOrBottomSheet';
import { useDefaultDuration, useDefaultKilometrage, useFormattedKilometrageLabel } from './utils';

import { DescriptionListContainer, SpecAndEditLinkContainer } from './styles/specs.styled';

enum ModalType {
	duration = 'duration',
	kilometrage = 'kilometrage',
}

type LeaseCarSpec = {
	key: string;
	value: React.ReactNode;
};

export const Specs = () => {
	const name = useSelector(leaseCarDataNameSelector);
	const leaseCarSpecs = useSelector(leaseCarDataSpecsSelector);
	const [modalType, setModalType] = useState<ModalType | null>(null);

	const { selectedKilometrage } = useDefaultKilometrage();
	const { selectedDuration } = useDefaultDuration();
	const formattedKilometrageLabel = useFormattedKilometrageLabel();
	const carContractABTest = getCarContractABTest();

	const clickEditHandler = (field: string) => {
		trackEvent({
			event: 'user_interaction',
			type: 'form',
			component: 'pop-up',
			field,
			action: 'click',
			actionValue: 'wijzig',
			funnel_name: 'private-lease-nieuw',
			funnel_step: 'samenstelling-auto-en-contract',
			funnel_index: '1',
		});
	};

	const editableKeys = {
		Looptijd: {
			modalType: ModalType.duration,
			selectedValue: selectedDuration,
			field: 'looptijd',
			format: (value: string | number) => `${value} maanden`,
		},
		'Kilometers per jaar': {
			modalType: ModalType.kilometrage,
			selectedValue: selectedKilometrage,
			field: 'kilometrage',
			format: () => formattedKilometrageLabel,
		},
	};

	const updatedSpecs = leaseCarSpecs
		.map((spec: LeaseCarSpec) => {
			const isEditable = Object.prototype.hasOwnProperty.call(editableKeys, spec.key);
			const editConfig = isEditable
				? editableKeys[spec.key as keyof typeof editableKeys]
				: undefined;
			if (carContractABTest && editConfig) {
				return {
					...spec,
					value: (
						<SpecAndEditLinkContainer>
							{editConfig.format(editConfig.selectedValue)}
							<Typography
								href="#"
								variant="link-text"
								onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
									event.preventDefault();
									clickEditHandler(editConfig.field);
									setModalType(editConfig.modalType);
								}}
							>
								wijzig
							</Typography>
						</SpecAndEditLinkContainer>
					),
				};
			}
			return spec;
		})
		.filter((spec: LeaseCarSpec) => {
			if (Object.prototype.hasOwnProperty.call(editableKeys, spec.key)) {
				return carContractABTest;
			}
			return true;
		});

	return (
		<>
			<DescriptionListContainer>
				<DescriptionList dataTest="description-list-specs" title={name} items={updatedSpecs} />
			</DescriptionListContainer>
			{modalType && (
				<ModalOrBottomSheet modalType={modalType} closeModal={() => setModalType(null)} />
			)}
		</>
	);
};
